import DOMPurify from "dompurify";

const PLACEHOLDER_TAG = "custom-element-placeholder";

export const CUSTOM_ELEMENT_TAGS = ["e-chart"];

/**
 * Extracts registered custom elements from HTML, replacing them with
 * indexed placeholders that are safe for DOMPurify.
 *
 * @param {string} html
 * @param {string[]} [tags=CUSTOM_ELEMENT_TAGS]
 * @returns {{ safeHtml: string, storedElements: string[] }}
 */
export const extractCustomElements = (html, tags = CUSTOM_ELEMENT_TAGS) => {
  const storedElements = [];
  let safeHtml = html;

  tags.forEach((tag) => {
    const pattern = new RegExp(
      `<${tag}(\\s[^>]*)?>([\\s\\S]*?)<\\/${tag}>|<${tag}(\\s[^>]*)?\\/?>`,
      "gi"
    );

    safeHtml = safeHtml.replace(pattern, (match) => {
      const idx = storedElements.length;
      storedElements.push(match);
      return `<${PLACEHOLDER_TAG} data-idx="${idx}"></${PLACEHOLDER_TAG}>`;
    });
  });

  return { safeHtml, storedElements };
};

/**
 * Replaces placeholder elements with the original custom elements.
 *
 * @param {string} html
 * @param {string[]} storedElements
 * @returns {string}
 */
export const restoreCustomElements = (html, storedElements) => {
  return html.replace(
    new RegExp(
      `<${PLACEHOLDER_TAG} data-idx="(\\d+)"><\\/${PLACEHOLDER_TAG}>`,
      "gi"
    ),
    (_, idx) => storedElements[Number(idx)] || ""
  );
};

/**
 * Full sanitization pipeline: extract custom elements, sanitize with
 * DOMPurify, then restore the originals.
 *
 * @param {string} rawHtml
 * @returns {string}
 */
export const sanitizeDashboardHtml = (rawHtml) => {
  const { safeHtml, storedElements } = extractCustomElements(rawHtml);

  const sanitizedHtml = DOMPurify.sanitize(safeHtml, {
    CUSTOM_ELEMENT_HANDLING: {
      tagNameCheck: (tagName) => tagName === PLACEHOLDER_TAG,
      attributeNameCheck: (attr) => attr === "data-idx",
      allowCustomizedBuiltInElements: false,
    },
  });

  return restoreCustomElements(sanitizedHtml, storedElements);
};
